import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, TextField, Typography, Paper, CircularProgress, Alert,
    Stack, Divider, Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';
import * as api from './paymentTermsApi';
import type { PaymentTerm, Language } from './paymentTermsApi';
import { sx14, paperSx, FormRow, DetailHeader, mergeLocalizations } from '../common/detailView';

interface Props {
    paymentTermId: number | null;
}

export function PaymentTermView({ paymentTermId }: Props) {
    const { t } = useTranslation();

    const [item, setItem] = useState<PaymentTerm | null>(null);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isNew, setIsNew] = useState(false);
    const [copy, setCopy] = useState<PaymentTerm | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const buildDefaultLoc = (lang: Language) => ({ language: lang });

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const langs = await api.getLanguages();
            setLanguages(langs);
            if (paymentTermId === null) {
                setItem({ id: 0, code: '', name: '', localizations: mergeLocalizations([], langs, buildDefaultLoc) });
                setIsNew(true);
                setIsEditing(true);
            } else {
                const data = await api.getPaymentTerm(paymentTermId);
                data.localizations = mergeLocalizations(data.localizations ?? [], langs, buildDefaultLoc);
                setItem(data);
                setIsNew(false);
                setIsEditing(false);
            }
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [paymentTermId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    if (error && !item) return <Alert severity="error">{error}</Alert>;
    if (!item) return null;

    const isCodeValid = (): boolean => !!item.code && item.code.length <= 20;

    const startEdit = () => { setCopy(JSON.parse(JSON.stringify(item))); setIsEditing(true); };
    const cancelEdit = () => { if (copy) setItem(copy); setIsEditing(false); };

    const save = async () => {
        setSaving(true); setError(null);
        try {
            const response = isNew ? await api.createPaymentTerm(item) : await api.updatePaymentTerm(item);
            response.localizations = mergeLocalizations(response.localizations ?? [], languages, buildDefaultLoc);
            setItem(response);
            setIsNew(false);
            setIsEditing(false);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const del = async () => {
        if (!window.confirm(t('paymentterm.confirmDelete'))) return;
        setSaving(true); setError(null);
        try {
            await api.deletePaymentTerm(item.id);
            window.history.back();
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const update = <K extends keyof PaymentTerm>(field: K, value: PaymentTerm[K]) => {
        setItem({ ...item, [field]: value });
    };

    const updateLocalization = (languageId: number, name: string) => {
        update('localizations', item.localizations.map(loc =>
            loc.language.id === languageId ? { ...loc, name } : loc
        ));
    };

    const sortedLocs = [...item.localizations].sort((a, b) =>
        (a.language.name ?? '').localeCompare(b.language.name ?? '')
    );

    return (
        <Paper variant="outlined" sx={paperSx}>
            <DetailHeader
                title={`${item.code} ${item.name}`}
                isEditing={isEditing}
                isNew={isNew}
                saving={saving}
                canSave={isCodeValid()}
                onEdit={startEdit}
                onSave={save}
                onCancel={cancelEdit}
                onDelete={del}
            />

            <Box p={2}>
                <Stack spacing={2}>
                    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                    <FormRow label={t('paymentterm.code')}>
                        {!isNew && !isEditing ? (
                            <Typography sx={sx14}>{item.code}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                inputProps={{ maxLength: 20, style: { textTransform: 'uppercase' } }}
                                value={item.code}
                                onChange={e => update('code', e.target.value.toUpperCase())}
                                error={!isCodeValid()} />
                        )}
                    </FormRow>

                    <FormRow label={t('paymentterm.name')}>
                        {!isEditing ? (
                            <Typography sx={sx14}>{item.name}</Typography>
                        ) : (
                            <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                value={item.name} onChange={e => update('name', e.target.value)} />
                        )}
                    </FormRow>

                    <Divider />

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold' }}>{t('common.language')}</TableCell>
                                <TableCell sx={{ ...sx14, fontWeight: 'bold' }}>{t('paymentterm.name')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedLocs.map(loc => (
                                <TableRow key={loc.language.id}>
                                    <TableCell sx={{ ...sx14, width: 150 }}>{loc.language.name}</TableCell>
                                    <TableCell>
                                        {!isEditing ? (
                                            <Typography sx={sx14}>{loc.name}</Typography>
                                        ) : (
                                            <TextField size="small" fullWidth inputProps={{ maxLength: 100 }}
                                                value={loc.name ?? ''}
                                                onChange={e => updateLocalization(loc.language.id, e.target.value)} />
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Stack>
            </Box>

            {saving && <Box display="flex" justifyContent="center" p={1}><CircularProgress size={20} /></Box>}
        </Paper>
    );
}
